import { IconButton, SxProps, Tooltip } from '@mui/material';
import { useCallback } from 'react';
import { Paths } from 'services/navigation/paths';
import LinkIcon from '@mui/icons-material/Link';
import { WorkflowBuilderIcon } from 'common/icons/WorkflowBuilderIcon';
import { workflowBuilderLink } from 'modules/layout/constants/navbar';

const linkButtonContainer: SxProps = {
  border: '1px solid',
  borderColor: 'customGrey.medium',
  padding: 0,
  px: 1,
  borderRadius: 1,
  color: 'primary.main'
};

// Mirrors the workflow-connection indicator already used on the Campaigns list
// (CampaignJobTitleCell) so a workflow-held job reads the same way everywhere.
const workflowIconButtonSx: SxProps = {
  ml: 0.5,
  p: 0.5
};

const workflowTooltipSx = {
  backgroundColor: 'customGrey.dark',
  color: 'common.white',
  fontSize: '14px',
  borderRadius: '16px',
  px: '12px',
  py: '4px',
  maxWidth: 250
};

const workflowTooltipArrowSx = {
  color: 'customGrey.dark'
};

type Props = {
  campaignId?: string;
  candidateBatchId?: string;
  isDraftCandidateBatch?: boolean;
  workflowId?: string;
};

export const LinkButton: React.FC<Props> = ({
  campaignId,
  candidateBatchId,
  isDraftCandidateBatch,
  workflowId
}) => {
  const navigateTo = useCallback(() => {
    if (candidateBatchId) {
      // Navigate to the ATS select ruleset setup page for draft analyses
      if (isDraftCandidateBatch) {
        window.open(`${Paths.ANALYSE_CREATE}/${candidateBatchId}`, '_blank');
        return;
      }
      // Navigate to the analysis page for launched analyses
      window.open(`${Paths.CANDIDATES_RANKING}/${candidateBatchId}`, '_blank');
      return;
    }

    if (campaignId) {
      window.open(`${Paths.OUTBOUND_CAMPAIGN}/${campaignId}`, '_blank');
    }
  }, [campaignId, candidateBatchId, isDraftCandidateBatch]);

  // A Campaign/Analysis holder takes precedence and keeps its existing link-out.
  // Only surface the Workflow indicator when a Workflow is the sole holder — it
  // opens the job's workflow in the Workflow Builder (popp-web-v2), matching how
  // the Campaigns list links out (CampaignJobTitleCell).
  if (workflowId && !campaignId && !candidateBatchId) {
    return (
      <Tooltip
        title="Connected to a workflow"
        placement="top"
        arrow
        componentsProps={{
          tooltip: { sx: workflowTooltipSx },
          arrow: { sx: workflowTooltipArrowSx }
        }}
      >
        <IconButton
          size="small"
          aria-label="Connected to a workflow"
          onClick={() =>
            window.open(`${workflowBuilderLink}/${workflowId}`, '_blank')
          }
          sx={workflowIconButtonSx}
        >
          <WorkflowBuilderIcon size={14} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <IconButton onClick={navigateTo} sx={linkButtonContainer}>
      <LinkIcon />
    </IconButton>
  );
};
